import { useI18nextInstance } from "@squide/i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { FlattenedComparison } from "#shared/comparisonSelection.ts";
import { useCachedManagerNames } from "@domains/reviews/client/components/reviewee-filters/useCachedOptionNames.ts";
import { usePeriodLabel } from "../../hooks/usePeriodLabel.ts";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";
import { assignSeriesLetters, BASE_SERIES_ID, BASE_SERIES_LETTER, describePeriod, toSwatchClassName } from "./comparisonSeries.ts";

export interface ComparisonSeries {
    id: string;
    position: number;
    label: string;
    letter: string;
    swatchClassName: string;
}

export type LabelledComparison = FlattenedComparison & { label?: string };

const SERIES_LABELS_NAMESPACE = "ComparisonSeriesLabels";

export function useComparisonSeries(entries: LabelledComparison[]): ComparisonSeries[] {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation(SERIES_LABELS_NAMESPACE, { i18n: i18nextInstance });
    const managerNames = useCachedManagerNames();
    const describePeriodLabel = usePeriodLabel();

    return useMemo(() => {
        const describeManagers = (managerIds: string[] | undefined) => {
            if (!managerIds?.length) {
                return undefined;
            }

            const soleName = managerIds.length === 1 ? managerNames.get(managerIds[0]) : undefined;

            return soleName ?? t("managerCount", { count: managerIds.length });
        };

        const letters = assignSeriesLetters(entries);
        const base: ComparisonSeries = { id: BASE_SERIES_ID, position: 0, label: t("base"), letter: BASE_SERIES_LETTER, swatchClassName: toSwatchClassName(0) };

        return [
            base,
            ...entries.map((entry, index) => {
                const position = index + 1;
                const parts = [describePeriodLabel(describePeriod(entry.input)), describeManagers(entry.input.managerIds)].filter(part => !!part);

                return {
                    id: entry.id,
                    position,
                    label: entry.label ?? (parts.length > 0 ? parts.join(t("partSeparator")) : t("fallback", { number: position })),
                    letter: letters[index],
                    swatchClassName: toSwatchClassName(position)
                };
            })
        ];
    }, [entries, managerNames, t, describePeriodLabel]);
}
